import { useState, useEffect, useCallback, useRef } from 'react';

const EC2_URL    = 'https://api.algrok.com';
const LAMBDA_URL = 'https://48vj6t5trl.execute-api.ap-south-1.amazonaws.com/prod';

function nowIST() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
}
function fmtTime(iso) {
  if (!iso) return '--';
  try { return new Date(iso).toLocaleTimeString('en-IN', { timeZone:'Asia/Kolkata', hour:'2-digit', minute:'2-digit', second:'2-digit' }); }
  catch { return '--'; }
}
function fmtDate(iso) {
  if (!iso) return '--';
  try { return new Date(iso).toLocaleDateString('en-IN', { timeZone:'Asia/Kolkata', day:'2-digit', month:'short', year:'numeric' }); }
  catch { return '--'; }
}

export default function TokenSetup() {
  var [status, setStatus]       = useState(null);
  var [ec2Health, setEc2Health] = useState(null);
  var [loading, setLoading]     = useState(true);
  var [loginLoading, setLoginLoading] = useState(false);
  var [loginMsg, setLoginMsg]   = useState('');
  var [loginType, setLoginType] = useState('');
  var [countdown, setCountdown] = useState('');
  var [statusSource, setStatusSource] = useState('');
  var initialFetchDone = useRef(false);

  /* ── Fetch status from EC2 first (fast), Lambda fallback ── */
  var fetchStatus = useCallback(function() {
    var done = false;

    // EC2 health — fast (~100ms)
    fetch(EC2_URL + '/health', { signal: AbortSignal.timeout(3000) })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setEc2Health(d);
        if (!done) {
          // Build a combined status from EC2 health
          setStatus(function(prev) {
            return Object.assign({}, prev, {
              ws_connected: d.ws_connected,
              instruments: d.instruments,
              positions: d.positions,
              ec2_ok: true,
            });
          });
        }
      })
      .catch(function() { setEc2Health({ status: 'offline' }); });

    // EC2 /token-status — fastest full status
    fetch(EC2_URL + '/token-status', { signal: AbortSignal.timeout(3000) })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (!d.error) {
          done = true;
          setStatus(d);
          setStatusSource('ec2');
          setLoading(false);
        }
      })
      .catch(function() {});

    // Lambda fallback — slower but more detailed
    fetch(LAMBDA_URL + '/token-status', { signal: AbortSignal.timeout(8000) })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (!done) {
          setStatus(d);
          setStatusSource('lambda');
        } else {
          // Merge Lambda details into EC2 status
          setStatus(function(prev) {
            return Object.assign({}, prev, {
              user_name: d.user_name || prev.user_name,
              login_method: d.login_method || prev.login_method,
              refreshed_at: d.refreshed_at || prev.refreshed_at,
              has_access_token: d.has_access_token !== undefined ? d.has_access_token : prev.has_access_token,
              fresh: d.fresh !== undefined ? d.fresh : prev.fresh,
            });
          });
        }
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }, []);

  /* ── Auto-exchange if redirected from OAuth ── */
  useEffect(function() {
    var pendingToken = sessionStorage.getItem('pending_request_token');
    if (pendingToken) {
      sessionStorage.removeItem('pending_request_token');
      setLoginMsg('OAuth redirect detected \u2014 exchanging token...');
      setLoginType('info');
      setLoginLoading(true);
      fetch(LAMBDA_URL + '/exchange-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_token: pendingToken }),
      })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.status === 'success') {
          setLoginMsg('\u2713 Token exchanged! Refreshing engine...');
          setLoginType('info');
          // Auto-refresh EC2
          refreshAndWaitForWS('\u2713 Full login! ' + d.user + ' | WebSocket connected!')
            .finally(function() { setLoginLoading(false); });
          return; // don't hit the finally below
        } else {
          setLoginMsg('\u2717 Exchange failed: ' + d.error);
          setLoginType('error');
        }
      })
      .catch(function(e) { setLoginMsg('\u2717 ' + e.message); setLoginType('error'); })
      .finally(function() { setLoginLoading(false); });
    }
  }, [fetchStatus]);

  useEffect(function() { fetchStatus(); }, [fetchStatus]);

  // Auto-refresh status every 15s
  useEffect(function() {
    var id = setInterval(fetchStatus, 15000);
    return function() { clearInterval(id); };
  }, [fetchStatus]);

  /* ── Countdown to auto-login ── */
  useEffect(function() {
    var id = setInterval(function() {
      var now = nowIST();
      var target = new Date(now);
      target.setHours(8, 16, 0, 0);
      if (now >= target) { setCountdown(''); return; }
      var diff = Math.floor((target - now) / 1000);
      var h = Math.floor(diff / 3600);
      var m = Math.floor((diff % 3600) / 60);
      var s = diff % 60;
      setCountdown((h > 0 ? h + 'h ' : '') + m + 'm ' + s + 's');
    }, 1000);
    return function() { clearInterval(id); };
  }, []);

  /* ── Quick Login (Playwright on EC2) ── */
  var doQuickLogin = function() {
    setLoginLoading(true);
    setLoginMsg('\u27F3 Running automated login on EC2... (~15 seconds)');
    setLoginType('info');

    fetch(EC2_URL + '/playwright-login', { signal: AbortSignal.timeout(60000) })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.status === 'success') {
          // Playwright already set the token on EC2 — verify WS is connected
          setLoginMsg('\u2713 Auto-login done! Verifying WebSocket...');
          setLoginType('info');
          return fetch(EC2_URL + '/health', { signal: AbortSignal.timeout(3000) })
            .then(function(r) { return r.json(); })
            .then(function(h) {
              if (h.ws_connected) {
                setLoginMsg('\u2713 Auto-login complete! ' + (d.user || '') + ' | WebSocket connected!');
                setLoginType('success');
                setEc2Health(h);
                fetchStatus();
              } else {
                // WS not connected — force refresh
                return refreshAndWaitForWS('\u2713 Auto-login done! ' + (d.user || '') + ' | WebSocket reconnected!');
              }
            })
            .catch(function() {
              return refreshAndWaitForWS('\u2713 Auto-login done! Reconnecting WebSocket...');
            });
        } else {
          throw new Error(d.error || 'Unknown error');
        }
      })
      .catch(function(e) {
        // EC2 playwright not available — try Lambda fallback
        setLoginMsg('EC2 playwright unavailable, trying Lambda login...');
        return fetch(LAMBDA_URL + '/manual-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        })
        .then(function(r) { return r.json(); })
        .then(function(d) {
          if (d.status === 'success') {
            setLoginMsg('\u2713 Login done! Refreshing engine...');
            setLoginType('info');
            return refreshAndWaitForWS('\u2713 Login complete! ' + (d.user || '') + ' | WebSocket connected!');
          } else {
            setLoginMsg('\u2717 Login failed: ' + (d.error || 'Unknown'));
            setLoginType('error');
          }
        })
        .catch(function(e2) {
          setLoginMsg('\u2717 All login methods failed: ' + e2.message);
          setLoginType('error');
        });
      })
      .finally(function() { setLoginLoading(false); });
  };

  /* ── Full Login (OAuth popup → manual Zerodha login) ── */
  var doFullLogin = function() {
    var apiKey = 'pet8g3l8tnzwxlgg';
    var redirectUrl = encodeURIComponent('https://api.algrok.com/oauth-callback');
    var oauthUrl = 'https://kite.trade/connect/login?api_key=' + apiKey + '&v=3';
    var popup = window.open(oauthUrl, 'KiteLogin', 'width=500,height=650,left=400,top=80');

    if (!popup) {
      setLoginMsg('\u2717 Popup blocked! Allow popups for this site and try again.');
      setLoginType('error');
      return;
    }

    setLoginMsg('\uD83C\uDF10 Zerodha login popup opened \u2014 complete login there...');
    setLoginType('info');
    setLoginLoading(true);

    var pollCount = 0;
    var maxPolls = 120; // 2 minutes max
    var detected = false;

    var poll = setInterval(function() {
      pollCount++;

      // Timeout after 2 minutes
      if (pollCount > maxPolls) {
        clearInterval(poll);
        setLoginMsg('\u2717 Login timed out. Popup may still be open \u2014 complete login and click "Check Status".');
        setLoginType('error');
        setLoginLoading(false);
        return;
      }

      // Check if popup closed
      if (!popup || popup.closed) {
        clearInterval(poll);
        if (!detected) {
          // Popup closed — check if login succeeded
          checkTokenAfterLogin();
        }
        return;
      }

      // Try to read popup URL (will fail cross-origin on kite.trade)
      try {
        var url = popup.location.href;
        if (url && (url.includes('api.algrok.com') || url.includes('request_token='))) {
          detected = true;
          clearInterval(poll);
          // Give EC2 a moment to process the callback
          setTimeout(function() {
            try { popup.close(); } catch(e) {}
            checkTokenAfterLogin();
          }, 2000);
        }
      } catch(e) {
        // Cross-origin — user is still on kite.trade, keep polling
      }
    }, 1000);
  };

  /* ── Check token after popup login ── */
  var checkTokenAfterLogin = function() {
    setLoginMsg('Checking login status...');
    setLoginType('info');

    // Check EC2 first — fastest signal
    fetch(EC2_URL + '/get-request-token', { signal: AbortSignal.timeout(5000) })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.request_token) {
          // EC2 captured the request token — exchange it
          setLoginMsg('Token captured! Exchanging for access_token...');
          return fetch(LAMBDA_URL + '/exchange-token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ request_token: d.request_token }),
          })
          .then(function(r) { return r.json(); })
          .then(function(d2) {
            if (d2.status === 'success') {
              // Auto-refresh EC2 and wait for WebSocket to reconnect
              return refreshAndWaitForWS('\u2713 Full login! ' + (d2.user || '') + ' | WebSocket reconnected!');
            } else {
              throw new Error(d2.error || 'Exchange failed');
            }
          });
        } else {
          // No request token on EC2 — check health for ws_connected
          return fetch(EC2_URL + '/health', { signal: AbortSignal.timeout(3000) })
            .then(function(r) { return r.json(); })
            .then(function(h) {
              if (h.ws_connected) {
                setLoginMsg('\u2713 Login successful! WebSocket connected.');
                setLoginType('success');
              } else {
                // Try Lambda status
                return fetch(LAMBDA_URL + '/token-status', { signal: AbortSignal.timeout(5000) })
                  .then(function(r) { return r.json(); })
                  .then(function(s) {
                    if (s.fresh || s.has_access_token) {
                      return refreshAndWaitForWS('\u2713 Login done! ' + (s.user_name || '') + ' | WebSocket reconnected!');
                    } else {
                      setLoginMsg('\u2717 Login not detected. Try Quick Login instead.');
                      setLoginType('error');
                    }
                  });
              }
            });
        }
      })
      .catch(function(e) {
        setLoginMsg('\u2717 Status check failed: ' + e.message + '. Try Quick Login.');
        setLoginType('error');
      })
      .finally(function() {
        setLoginLoading(false);
        fetchStatus();
      });
  };

  /* ── Force Refresh (tell EC2 to reload token + reconnect WS) ── */
  var refreshAndWaitForWS = function(successMsg) {
    setLoginMsg('\u27F3 Reloading token on engine + reconnecting WebSocket...');
    setLoginType('info');

    return fetch(EC2_URL + '/refresh', { signal: AbortSignal.timeout(15000) })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.status !== 'ok') throw new Error(d.message || 'Refresh failed');
        setLoginMsg('\u27F3 Token reloaded \u2014 waiting for WebSocket...');
        // Poll /health until ws_connected = true (max 20s)
        return new Promise(function(resolve) {
          var attempts = 0;
          var maxAttempts = 20;
          var pollWS = setInterval(function() {
            attempts++;
            fetch(EC2_URL + '/health', { signal: AbortSignal.timeout(2000) })
              .then(function(r) { return r.json(); })
              .then(function(h) {
                if (h.ws_connected) {
                  clearInterval(pollWS);
                  setLoginMsg(successMsg || '\u2713 Login complete! WebSocket connected.');
                  setLoginType('success');
                  setEc2Health(h);
                  resolve(true);
                } else if (attempts >= maxAttempts) {
                  clearInterval(pollWS);
                  setLoginMsg('\u2713 Token refreshed but WebSocket still reconnecting. May take a few seconds.');
                  setLoginType('success');
                  resolve(false);
                }
              })
              .catch(function() {
                if (attempts >= maxAttempts) {
                  clearInterval(pollWS);
                  resolve(false);
                }
              });
          }, 1000);
        });
      })
      .catch(function(e) {
        setLoginMsg('\u2717 Refresh error: ' + e.message);
        setLoginType('error');
      })
      .finally(function() {
        fetchStatus();
      });
  };

  var doForceRefresh = function() {
    setLoginLoading(true);
    setLoginMsg('\u27F3 Reloading token on EC2 and reconnecting WebSocket...');
    setLoginType('info');

    fetch(EC2_URL + '/refresh', { signal: AbortSignal.timeout(15000) })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.status === 'ok') {
          setLoginMsg('\u2713 Token reloaded! WebSocket reconnecting...');
          setLoginType('success');
        } else {
          setLoginMsg('\u2717 Refresh failed: ' + (d.message || JSON.stringify(d)));
          setLoginType('error');
        }
      })
      .catch(function(e) {
        setLoginMsg('\u2717 Refresh error: ' + e.message);
        setLoginType('error');
      })
      .finally(function() {
        setLoginLoading(false);
        setTimeout(fetchStatus, 3000);
      });
  };

  /* ── Derived state ── */
  var now = nowIST();
  var h = now.getHours(), m = now.getMinutes();
  var isFresh = status ? status.fresh : false;
  var isPast816 = h > 8 || (h === 8 && m >= 16);
  var isAfter8 = h >= 8;
  var wsConnected = ec2Health ? ec2Health.ws_connected : (status ? status.ws_connected : false);
  var hasAccessToken = status ? status.has_access_token : false;
  var showBanner = !isFresh && isAfter8 && !isPast816;
  var msgColor = loginType === 'success' ? '#00d4aa' : loginType === 'error' ? '#ff4466' : '#0ea5e9';

  /* ── Styles ── */
  var pill = function(active, color) {
    color = color || '14,165,233';
    return {
      padding: '3px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700,
      background: active ? 'rgba(' + color + ',0.2)' : 'rgba(26,45,74,0.2)',
      color: active ? 'rgb(' + color + ')' : '#6b7a8d',
      border: 'none', display: 'inline-block',
    };
  };

  return (
    <div style={{ padding: 24, background: '#080e1d', minHeight: '100%', color: '#e2e8f0', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ maxWidth: 760 }}>

        {/* Warning Banner */}
        {showBanner && (
          <div style={{ marginBottom: 20, padding: '14px 20px', borderRadius: 8,
            background: 'rgba(251,191,36,0.1)', border: '2px solid rgba(251,191,36,0.5)',
            display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 24 }}>{'\u26A0\uFE0F'}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24' }}>LOGIN REQUIRED</div>
              <div style={{ fontSize: 11, color: '#d4a847' }}>
                Token not refreshed today. Auto-login triggers in <b>{countdown || 'soon'}</b>.
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Token Setup</div>
            <div style={{ fontSize: 10, color: '#556677' }}>
              Daily login \u00B7 Auto at 8:16 AM IST
              {statusSource && (<span style={pill(true, statusSource === 'ec2' ? '0,212,170' : '251,191,36')}> via {statusSource}</span>)}
            </div>
          </div>
          <button onClick={fetchStatus} disabled={loading}
            style={{ padding: '6px 14px', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              background: 'rgba(14,165,233,0.1)', color: '#0ea5e9', border: '1px solid rgba(14,165,233,0.3)',
              fontFamily: 'inherit' }}>
            {loading ? '\u27F3' : '\u27F3'} Refresh Status
          </button>
        </div>

        {/* Token Status Card */}
        <div style={{ marginBottom: 20, padding: 20, borderRadius: 8, background: '#0d1526',
          border: '1px solid ' + (isFresh ? 'rgba(0,212,170,0.3)' : 'rgba(255,68,102,0.3)') }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 32 }}>{loading ? '\u27F3' : isFresh ? '\u2705' : '\u274C'}</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: isFresh ? '#00d4aa' : '#ff4466' }}>
                {loading ? 'Checking...' : isFresh ? 'Token Active' : 'Token Not Refreshed Today'}
              </div>
              <div style={{ fontSize: 11, color: '#6b7a8d' }}>
                {status && status.user_name ? status.user_name : '--'} \u00B7 Last: {fmtDate(status && status.refreshed_at)} {fmtTime(status && status.refreshed_at)}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
            {[
              { label: 'ENCTOKEN', value: isFresh ? '\u2713 Active' : '\u2717 Expired', sub: 'REST API', color: isFresh ? '#00d4aa' : '#ff4466' },
              { label: 'ACCESS TOKEN', value: hasAccessToken ? '\u2713 Active' : '\u26A0 Missing', sub: 'OAuth token', color: hasAccessToken ? '#00d4aa' : '#fbbf24' },
              { label: 'WEBSOCKET', value: wsConnected ? '\u2713 Connected' : '\u2717 Disconnected', sub: 'Live data', color: wsConnected ? '#00d4aa' : '#ff4466' },
              { label: 'LOGIN METHOD', value: status && status.login_method === 'manual' ? '\uD83D\uDC64 Manual' : status && status.login_method === 'auto' ? '\uD83E\uDD16 Auto' : '--', sub: "Today's login", color: '#e2e8f0' },
            ].map(function(card, i) {
              return (
                <div key={i} style={{ padding: '10px 12px', borderRadius: 6,
                  background: 'rgba(14,165,233,0.04)', border: '1px solid rgba(26,45,74,0.3)' }}>
                  <div style={{ fontSize: 9, color: '#556677', marginBottom: 3, fontWeight: 800, letterSpacing: '0.5px' }}>{card.label}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: card.color }}>{card.value}</div>
                  <div style={{ fontSize: 9, color: '#3d4f63' }}>{card.sub}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Login Options */}
        <div style={{ marginBottom: 20, padding: 20, borderRadius: 8, background: '#0d1526',
          border: '1px solid rgba(26,45,74,0.5)' }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6, color: '#0ea5e9', letterSpacing: '0.5px' }}>LOGIN NOW</div>
          <div style={{ fontSize: 10, color: '#556677', marginBottom: 16 }}>
            Works anytime. Quick Login is fully automated. Full Login opens Zerodha in a popup.
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>

            {/* Quick Login */}
            <div style={{ flex: 1, minWidth: 200, padding: 16, borderRadius: 8,
              background: 'rgba(0,212,170,0.04)', border: '1px solid rgba(0,212,170,0.2)' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#00d4aa', marginBottom: 6 }}>{'\uD83D\uDD11'} Quick Login</div>
              <div style={{ fontSize: 10, color: '#6b7a8d', marginBottom: 12, lineHeight: '16px' }}>
                Playwright auto-login on EC2.<br/>
                Gets enctoken + access_token.<br/>
                No popup needed. ~15 seconds.
              </div>
              <button onClick={doQuickLogin} disabled={loginLoading}
                style={{ width: '100%', padding: '8px', borderRadius: 5, fontSize: 11, fontWeight: 800,
                  cursor: loginLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  background: 'rgba(0,212,170,0.15)', color: '#00d4aa',
                  border: '1px solid rgba(0,212,170,0.4)' }}>
                {loginLoading ? '\u27F3 Logging in...' : 'Quick Login'}
              </button>
            </div>

            {/* Full Login */}
            <div style={{ flex: 1, minWidth: 200, padding: 16, borderRadius: 8,
              background: 'rgba(14,165,233,0.04)', border: '2px solid rgba(14,165,233,0.3)' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#0ea5e9', marginBottom: 6 }}>
                {'\uD83C\uDF10'} Full Login <span style={{ fontSize: 8, color: '#00d4aa', marginLeft: 4, background: 'rgba(0,212,170,0.1)', padding: '1px 5px', borderRadius: 2 }}>RECOMMENDED</span>
              </div>
              <div style={{ fontSize: 10, color: '#6b7a8d', marginBottom: 12, lineHeight: '16px' }}>
                Opens Zerodha popup. You login normally.<br/>
                Captures access_token for WebSocket.<br/>
                Best for manual daily login.
              </div>
              <button onClick={doFullLogin} disabled={loginLoading}
                style={{ width: '100%', padding: '8px', borderRadius: 5, fontSize: 11, fontWeight: 800,
                  cursor: loginLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  background: 'rgba(14,165,233,0.15)', color: '#0ea5e9',
                  border: '1px solid rgba(14,165,233,0.4)' }}>
                Full Login (Popup)
              </button>
            </div>

            {/* Force Refresh */}
            <div style={{ flex: 1, minWidth: 200, padding: 16, borderRadius: 8,
              background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.2)' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#a78bfa', marginBottom: 6 }}>{'\uD83D\uDD04'} Force Refresh</div>
              <div style={{ fontSize: 10, color: '#6b7a8d', marginBottom: 12, lineHeight: '16px' }}>
                Reload token on EC2 + reconnect WebSocket.<br/>
                Use after login if WS shows disconnected.<br/>
                No re-login needed.
              </div>
              <button onClick={doForceRefresh} disabled={loginLoading}
                style={{ width: '100%', padding: '8px', borderRadius: 5, fontSize: 11, fontWeight: 800,
                  cursor: loginLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  background: 'rgba(167,139,250,0.12)', color: '#a78bfa',
                  border: '1px solid rgba(167,139,250,0.3)' }}>
                Force Refresh
              </button>
            </div>

          </div>

          {/* Login message */}
          {loginMsg && (
            <div style={{ padding: '10px 14px', borderRadius: 6, fontSize: 11, lineHeight: '16px',
              background: 'rgba(' + (loginType === 'success' ? '0,212,170' : loginType === 'error' ? '255,68,102' : '14,165,233') + ',0.08)',
              border: '1px solid rgba(' + (loginType === 'success' ? '0,212,170' : loginType === 'error' ? '255,68,102' : '14,165,233') + ',0.2)',
              color: msgColor }}>
              {loginMsg}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div style={{ marginBottom: 20, padding: 20, borderRadius: 8, background: '#0d1526',
          border: '1px solid rgba(26,45,74,0.5)' }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 14, color: '#0ea5e9', letterSpacing: '0.5px' }}>DAILY SCHEDULE</div>
          {[
            { time: '8:00 AM', icon: '\u23F0', label: 'Login Reminder', desc: 'Telegram notification sent', done: h >= 8 },
            { time: '8:00\u20148:15', icon: '\uD83D\uDC64', label: 'Manual Login Window', desc: 'Login via dashboard during this window', done: isFresh && (status && status.login_method === 'manual') },
            { time: '8:16 AM', icon: '\uD83E\uDD16', label: 'Auto-login', desc: 'If not done \u2192 auto-login triggers', done: isPast816 && isFresh },
            { time: '8:16 AM', icon: '\uD83D\uDCE1', label: 'WebSocket Connect', desc: 'Live data stream connects', done: isPast816 && wsConnected },
            { time: '9:15 AM', icon: '\uD83D\uDCC8', label: 'Market Opens', desc: 'Algos start monitoring signals', done: h >= 9 && m >= 15 },
          ].map(function(step, i) {
            return (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 72, flexShrink: 0, fontSize: 10, color: '#3d4f63', paddingTop: 2 }}>{step.time}</div>
                <div style={{ width: 22, fontSize: 15 }}>{step.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: step.done ? '#00d4aa' : '#e2e8f0' }}>{step.label}</div>
                  <div style={{ fontSize: 9, color: '#3d4f63' }}>{step.desc}</div>
                </div>
                <div style={{ fontSize: 13 }}>{step.done ? '\u2705' : '\u23F3'}</div>
              </div>
            );
          })}
          {countdown && !isFresh && (
            <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 5,
              background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)',
              fontSize: 11, color: '#fbbf24' }}>
              {'\u23F1'} Auto-login triggers in: <b>{countdown}</b>
            </div>
          )}
          {isPast816 && !isFresh && (
            <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 5,
              background: 'rgba(255,68,102,0.08)', border: '1px solid rgba(255,68,102,0.2)',
              fontSize: 11, color: '#ff4466' }}>
              {'\u274C'} Auto-login window passed \u2014 login manually now!
            </div>
          )}
        </div>

        {/* EC2 Health */}
        {ec2Health && (
          <div style={{ padding: 16, borderRadius: 8, background: 'rgba(14,165,233,0.04)',
            border: '1px solid rgba(26,45,74,0.3)', fontSize: 10, color: '#556677' }}>
            <div style={{ fontWeight: 800, color: '#6b7a8d', marginBottom: 8 }}>EC2 ENGINE STATUS</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
              {[
                { label: 'Engine', value: ec2Health.status === 'ok' ? '\u2713 Running' : '\u2717 Down', color: ec2Health.status === 'ok' ? '#00d4aa' : '#ff4466' },
                { label: 'WebSocket', value: ec2Health.ws_connected ? '\u2713 Connected' : '\u2717 Down', color: ec2Health.ws_connected ? '#00d4aa' : '#ff4466' },
                { label: 'Instruments', value: String(ec2Health.instruments || 0), color: '#e2e8f0' },
                { label: 'Positions', value: String(ec2Health.positions || 0), color: '#e2e8f0' },
              ].map(function(item, i) {
                return (
                  <div key={i}>
                    <div style={{ fontSize: 9, color: '#3d4f63', marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: item.color }}>{item.value}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
